import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import axios from 'axios';

import { API_URL as API } from '../config';
import secureStorage from '../utils/secureStorage';

// Individual sortable item component
const SortableItem = ({ id, children, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <div
        {...attributes}
        {...listeners}
        className="absolute left-0 top-1/2 -translate-y-1/2 -ml-6 p-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity z-10"
        data-testid={`drag-handle-${id}`}
      >
        <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-200" />
      </div>
      {children}
    </div>
  );
};

// Main sortable list component
const SortableEntryList = ({ 
  entries, 
  setEntries, 
  reorderEndpoint, 
  renderEntry,
  onReorderComplete 
}) => {
  const [activeId, setActiveId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      const oldIndex = entries.findIndex((item) => item.id === active.id);
      const newIndex = entries.findIndex((item) => item.id === over.id);
      
      const newEntries = arrayMove(entries, oldIndex, newIndex);
      setEntries(newEntries);

      // Save new order to backend
      try {
        setIsSaving(true);
        const token = secureStorage.getToken();
        await axios.put(
          `${API}/${reorderEndpoint}`,
          { ordered_ids: newEntries.map(e => e.id) },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (onReorderComplete) {
          onReorderComplete();
        }
      } catch (error) {
        console.error('Error saving order:', error);
        // Revert on error
        setEntries(entries);
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (!entries || entries.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      {isSaving && (
        <div className="absolute top-0 right-0 text-xs text-orange-400 animate-pulse">
          Saving order...
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={entries.map(e => e.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3 ml-6">
            {entries.map((entry) => (
              <SortableItem 
                key={entry.id} 
                id={entry.id}
                isDragging={activeId === entry.id}
              >
                {renderEntry(entry)}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default SortableEntryList;
